import React, { Component } from 'react';
import PropTypes from 'prop-types';
import moment from 'moment';
import defaultJobImage from '../../../images/default_skill.jpeg';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faClipboardCheck, faExclamationCircle, faAngleDown, faAngleUp } from '@fortawesome/free-solid-svg-icons/index';
import { Tooltip } from 'reactstrap';
import { jobStatuses } from '../../../constants/jobs';
import { labels } from '../../../constants/text';
import { formatPrice } from '../../../helpers/formatData';
import { getEstCost } from '../../../helpers/general';
import JobBulletsMenu from './JobBulletsMenu';
import { appPath } from '../../../constants';
import UsersService from '../../../services/UsersService';
import { getCancelJobConfirmationMessage, isJobNotEditable, needToHideStatusBadge } from '../../../helpers/jobs';
import { uiDateFormatFull } from '../../../constants/date';
import { getNowInTimeZone, renderDateWithTimeZone } from '../../../helpers/timezones';

export default class JobInfo extends Component {
  static propTypes = {
    jobData: PropTypes.object.isRequired,
    history: PropTypes.object.isRequired,
    isPastJobsList: PropTypes.bool,
    isWorkerDetails: PropTypes.bool,
    isEventView: PropTypes.bool,
    cancelJob: PropTypes.func,
    toggleEventJobWorkersSection: PropTypes.func,
    jobWorkersAreShown: PropTypes.bool
  };

  state = {
    jobBoardTooltip: false,
    multiDayTooltip: false
  };

  toggleTooltip = name => {
    this.setState(prevState => ({
      [name]: !prevState[name]
    }));
  };

  navToEvent = e => {
    e.stopPropagation();
    const { history, jobData: { event_id } } = this.props;
    history.push(`/${appPath}/events/${event_id}`);
  };

  navToDetails = () => {
    const { history, jobData: { id } } = this.props;
    history.push(`/${appPath}/jobs/${id}`);
  };

  cancelThisJob = () => {
    const { start_date, id, status, hired_workers } = this.props.jobData;
    const confirmMessageText = getCancelJobConfirmationMessage(UsersService.getUserRole(), status, start_date, hired_workers);

    this.props.UIShowConfirm(
      confirmMessageText,
      () => this.props.cancelJob(id),
      { cancelButtonLabel: 'No', proceedButtonLabel: 'Yes' }
    );
  };

  _renderBadge = () => {
    const { is_lta, status } = this.props.jobData;
    const label = is_lta ? labels.lta : labels.event;
    return (
      <div className='badge-wrapper-job-list'>
        <div
          className={`badge-job badge-${status} badge-job-list`}
          onClick={this.navToEvent}
        >
          {label}
        </div>
      </div>
    );
  };

  render () {
    const { jobBoardTooltip, multiDayTooltip } = this.state;
    const { jobData, isWorkerDetails, isEventView, toggleEventJobWorkersSection, jobWorkersAreShown } = this.props;
    let { isPastJobsList } = this.props;
    if (isEventView) {
      isPastJobsList = moment(jobData.start_date).isBefore(getNowInTimeZone(jobData.time_zone_id), 'day');
    }
    const { id, icon_url, skill_label, company_name, location_name, is_post_to_board, job_cost_rate, hours, quantity_of_workers,
      hired_workers, status, timeShiftUI, total_payment, is_approved, approved_at, time_zone_id, event_id, is_multi_day,
      workers, owner_first_name, owner_last_name } = jobData;
    const estCost = getEstCost(job_cost_rate, hours, quantity_of_workers) || '';
    const hasFinalCost = total_payment || total_payment === 0;
    const jobIsNotEditable = isJobNotEditable(status, is_approved);
    const hideStatusBadge = needToHideStatusBadge(isPastJobsList, status, hired_workers > 0);
    const showNeedApproveLabel = hideStatusBadge && !is_approved;
    const isAdmin = UsersService.isAdmin();
    const showMultiDayIcon = !isAdmin && is_multi_day && !isEventView;
    return (
      <div className={`job-wrapper status-${status} row position-relative`} onClick={this.navToDetails}>
        { isEventView && workers && workers.length > 0 && (
          <div className='caret-show-workers' onClick={toggleEventJobWorkersSection}>
            <FontAwesomeIcon icon={jobWorkersAreShown ? faAngleUp : faAngleDown} />
          </div>
        )}
        <div className='py-1 col-9 col-sm-5 col-md-3 d-flex align-items-center align-self-stretch position-relative'>
          <div>
            { isAdmin && event_id && !isEventView && this._renderBadge()}
            <div
              className='job-image'
              style={{ backgroundImage: `url('${icon_url || defaultJobImage}')` }}
            />
          </div>
          <div>
            <h5>{skill_label}</h5>
            { company_name && <p className='pt-1 text-muted font-weight-bold'>{ company_name }
              <span className='font-italic'> ({ owner_first_name } { owner_last_name })</span></p> }
          </div>
        </div>
        <div className='col-3 col-sm-1'>
          { showMultiDayIcon && (
            <React.Fragment>
              <div id={`multi-day-tooltip-target-${id}`} className='multi-day-icon' onClick={this.navToEvent}>M</div>
              <Tooltip
                placement='top'
                isOpen={multiDayTooltip}
                target={`multi-day-tooltip-target-${id}`}
                toggle={() => this.toggleTooltip('multiDayTooltip')}
              >
                Multiday/multishifts
              </Tooltip>
            </React.Fragment>
          )}
        </div>
        <div className='py-1 col-6 col-sm-3 col-md-2'>
          <p>{ timeShiftUI }</p>
          <p className='text-muted'>Time</p>
        </div>
        <div className='col-6 col-sm-3 col-md-1'>
          <p>{location_name}</p>
          <p className='text-muted'>Location</p>
        </div>
        <div className={`py-1 col-6 col-sm-1 col-md-1 ${isPastJobsList ? 'd-md-none' : ''}`} >
          { !isPastJobsList && is_post_to_board && (
            <React.Fragment>
              <div id={`job-board-tooltip-target-${id}`} className='post-to-board-icon text-sm-center'>
                <FontAwesomeIcon size='lg' icon={faClipboardCheck} />
              </div>
              <Tooltip
                placement='top'
                isOpen={jobBoardTooltip}
                target={`job-board-tooltip-target-${id}`}
                toggle={() => this.toggleTooltip('jobBoardTooltip')}
              >
                Posted to Job Board
              </Tooltip>
            </React.Fragment>
          )}
        </div>
        <div className='py-1 col-6 col-sm-4 col-md-1'>
          <p>{hired_workers}/<strong>{quantity_of_workers}</strong></p>
          <p className='text-muted'>Workers</p>
        </div>
        <div className='py-1 d-none d-sm-block col-sm-4 col-md-1'>
          <p>{hasFinalCost ? formatPrice(total_payment) : estCost}</p>
          <p className='text-muted'>{ hasFinalCost ? 'Final Cost' : 'Billing Est.'}</p>
        </div>
        <div className={`py-1 pr-0 col-6 col-sm-2 col-md-1 d-flex justify-content-start justify-content-md-end ${isPastJobsList ? 'col-lg-2' : ''}`}>
          { !hideStatusBadge && (
            <div className={`status-btn ${status}`}>{jobStatuses[status] ? jobStatuses[status].ui : ''}</div>
          )}
          { is_approved && hideStatusBadge && (
            <div className={`status-btn approved ${isAdmin ? 'has-tooltip' : ''}`}>
              <span>Approved</span>
              {isAdmin && (
                <div className='ui-tooltip'>
                  <strong>Approved at:</strong>
                  <div>{renderDateWithTimeZone(approved_at, uiDateFormatFull, time_zone_id)}</div>
                </div>
              )}
            </div>
          )}
          { showNeedApproveLabel && (
            <p className='text-left text-md-right need-approve-label'>
              <span className='icon-wrapper exclamation-icon'>
                <FontAwesomeIcon icon={faExclamationCircle} />
              </span>
              {labels.needApproveLabel}
            </p>
          )}
        </div>
        <div className='py-1 col-6 col-sm-1 d-flex justify-content-start justify-content-md-end'>
          <JobBulletsMenu
            jobIsNotEditable={jobIsNotEditable}
            navToDetails={this.navToDetails}
            cancelThisJob={this.cancelThisJob}
            isWorkerDetails={isWorkerDetails}
          />
        </div>
      </div>
    );
  }
}



// WEBPACK FOOTER //
// ./src/components/app/jobsList/JobInfo.js